import * as React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { useInvokeAction } from '@leagueplatform/masonry';
import {
  Button,
  StackItem,
  StackLayout,
  spacingTokenMap,
} from '@leagueplatform/genesis-core';
import type { GDSSpaceToken } from '@leagueplatform/genesis-core';

type AllRecordedDataListProperties = {
  nextPageCursor?: string;
  loadMoreLabel?: string;
  itemSpacing?: string;
};

type AllRecordedDataListSections = {
  paginatedItems: MasonryEngineNode[];
};

type AllRecordedDataListNode = MasonryEngineNode<
  'allRecordedDataList',
  AllRecordedDataListProperties,
  Record<string, never>,
  AllRecordedDataListSections
>;

export type AllRecordedDataListRendererProps =
  MasonryEngineNodeRendererProps<AllRecordedDataListNode>;

function isValidSpacingToken(value: string): value is GDSSpaceToken {
  return Object.hasOwn(spacingTokenMap, value);
}

export function AllRecordedDataListRenderer({
  paginatedItems = [],
  nextPageCursor,
  loadMoreLabel,
  itemSpacing,
  nodeId,
  isReloading,
  rootElementRef,
  locatorId,
}: AllRecordedDataListRendererProps) {
  const invokeAction = useInvokeAction();
  const [accumulatedItems, setAccumulatedItems] =
    React.useState<React.ReactElement[]>(paginatedItems);
  const lastProcessedCursorRef = React.useRef<string | undefined>(
    nextPageCursor,
  );

  React.useEffect(() => {
    if (isReloading || paginatedItems.length === 0) return;

    if (lastProcessedCursorRef.current === nextPageCursor) return;

    setAccumulatedItems((prev) =>
      prev.length === 0 ? paginatedItems : [...prev, ...paginatedItems],
    );
    lastProcessedCursorRef.current = nextPageCursor;
  }, [isReloading, paginatedItems, nextPageCursor]);

  const handleLoadMore = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!nextPageCursor) return;

      invokeAction({
        event,
        type: 'reload',
        namespace: 'default',
        payload: {
          renderIntermediately: 'self',
          nodeIds: [nodeId],
          extraQueryParams: [{ key: 'cursor', value: nextPageCursor }],
        },
      });
    },
    [invokeAction, nodeId, nextPageCursor],
  );

  const spacing =
    itemSpacing && isValidSpacingToken(itemSpacing)
      ? spacingTokenMap[itemSpacing]
      : '$none';

  return (
    <StackLayout
      ref={rootElementRef}
      data-testid={locatorId}
      spacing={spacing}
      horizontalAlignment="stretch"
    >
      {accumulatedItems}
      {nextPageCursor && (
        <StackItem horizontalAlignment="center">
          <Button
            onClick={handleLoadMore}
            loading={isReloading}
            disabled={isReloading}
            size="medium"
            priority="primary"
          >
            {loadMoreLabel || 'Load More'}
          </Button>
        </StackItem>
      )}
    </StackLayout>
  );
}
